export type MarketingAttribution = {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  landingPath?: string;
  landingUrl?: string;
  referrer?: string;
  capturedAt?: string;
};

const ATTRIBUTION_STORAGE_KEY = "approbot_marketing_attribution_v1";

function cleanValue(value: string | null, maxLength = 200): string | null {
  if (!value) return null;
  const cleaned = value.trim().slice(0, maxLength);
  return cleaned.length ? cleaned : null;
}

function toOptional(value: string | null): string | undefined {
  return value || undefined;
}

function hasAttributionValues(value: MarketingAttribution): boolean {
  return Boolean(
    value.gclid ||
      value.gbraid ||
      value.wbraid ||
      value.utmSource ||
      value.utmMedium ||
      value.utmCampaign ||
      value.utmTerm ||
      value.utmContent
  );
}

export function getStoredMarketingAttribution(): MarketingAttribution | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as MarketingAttribution;
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

export function captureMarketingAttribution(pathname: string, search: string): MarketingAttribution | null {
  if (typeof window === "undefined") return null;

  const params = new URLSearchParams(search);
  const captured: MarketingAttribution = {
    gclid: toOptional(cleanValue(params.get("gclid"))),
    gbraid: toOptional(cleanValue(params.get("gbraid"))),
    wbraid: toOptional(cleanValue(params.get("wbraid"))),
    utmSource: toOptional(cleanValue(params.get("utm_source"))),
    utmMedium: toOptional(cleanValue(params.get("utm_medium"))),
    utmCampaign: toOptional(cleanValue(params.get("utm_campaign"))),
    utmTerm: toOptional(cleanValue(params.get("utm_term"))),
    utmContent: toOptional(cleanValue(params.get("utm_content"))),
  };

  const previous = getStoredMarketingAttribution();
  const merged: MarketingAttribution = {
    ...(previous || {}),
    ...Object.fromEntries(
      Object.entries(captured).filter(([, value]) => typeof value === "string" && value.length > 0)
    ),
  };

  if (!hasAttributionValues(merged)) return previous || null;

  merged.landingPath = toOptional(cleanValue(pathname, 300)) || previous?.landingPath;
  merged.landingUrl = toOptional(cleanValue(window.location.href, 600)) || previous?.landingUrl;
  if (!previous?.referrer) {
    merged.referrer = toOptional(cleanValue(document.referrer, 600));
  } else {
    merged.referrer = previous.referrer;
  }
  merged.capturedAt = new Date().toISOString();

  window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(merged));
  return merged;
}

export function getStoredAttributionPayload(): {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  landingPath?: string;
  landingUrl?: string;
  initialReferrer?: string;
  capturedAtMs?: number;
} | null {
  const stored = getStoredMarketingAttribution();
  if (!stored) return null;
  return {
    gclid: stored.gclid || undefined,
    gbraid: stored.gbraid || undefined,
    wbraid: stored.wbraid || undefined,
    utmSource: stored.utmSource || undefined,
    utmMedium: stored.utmMedium || undefined,
    utmCampaign: stored.utmCampaign || undefined,
    utmTerm: stored.utmTerm || undefined,
    utmContent: stored.utmContent || undefined,
    landingPath: stored.landingPath || undefined,
    landingUrl: stored.landingUrl || undefined,
    initialReferrer: stored.referrer || undefined,
    capturedAtMs: stored.capturedAt ? Date.parse(stored.capturedAt) || undefined : undefined,
  };
}
